import { Box, Flex } from "@chakra-ui/react";
import {
  buildQuestionnaireAnswerTelemetry,
  useTrackEvent,
  useTrackingEvents,
  useInResiStore,
} from "@superbright/indexeddb-orm";
import type { Filters } from "@superbright/indexeddb-orm";
import { useEffect, useRef, useState } from "react";

import { useQuestionnaireMutation } from "api/questionnaire/useQuestionnaire";
import { useSaveFilters } from "api/save_filters/useSaveFilters";
import type {
  FiltersResponse,
  PropertyResponse,
  Questionnaire as QuestionnaireItem,
} from "api/schemas";
import { QuestionnaireAllSet } from "components";
import { QuestionStepRenderer } from "features/questionnaire/components/QuestionStepRenderer";
import { useAnalyticsReady, useIsMobile, useIsEmbedded } from "utils";

import { CoverMedia } from "./components/CoverMedia";

export const QuestionnaireView = ({
  propertyData,
  filtersData,
  setFiltersParams,
  setUnitsEnabled,
}: {
  propertyData: PropertyResponse;
  filtersData: FiltersResponse;
  setFiltersParams?: (filters: Filters) => void;
  setUnitsEnabled?: (enabled: boolean) => void;
}) => {
  const isMobile = useIsMobile();
  const store = useInResiStore();
  const propertyId = String(propertyData.data.id);
  const propertySlug = propertyData.data.slug;
  const propertyName = propertyData.data.name;
  const totalUnitsAvailable = propertyData.data._count?.units ?? 0;
  const { isEmbedded, height } = useIsEmbedded();

  const {
    trackViewAllUnits,
    trackQuestionnaireSkip,
    trackQuestionnaireBack,
    trackQuestionnaireContinue,
  } = useTrackingEvents();
  const trackEvent = useTrackEvent();
  const analyticsReady = useAnalyticsReady();
  const lastQuestionnairePageRef = useRef<string | null>(null);
  const questionnaireMutation = useQuestionnaireMutation(propertySlug);
  const saveFiltersMutation = useSaveFilters(propertySlug);

  const saveQuestionnaireData = () => {
    const questions = filtersData?.data?.questionnaire ?? [];
    const tempFilters = store.tempFilters as Record<string, unknown>;
    const selectedQuestions = questions
      .filter((q: QuestionnaireItem) => {
        const val = tempFilters[q.name];
        if (val === null || val === undefined) return false;
        if (Array.isArray(val)) return val.length > 0;
        return true;
      })
      .map((q: QuestionnaireItem) => {
        const val = tempFilters[q.name];
        if (q.component === "slider" || !Array.isArray(val)) return q;
        const selectedValues = new Set(
          (val as Array<{ value: unknown }>).map((item) => String(item.value))
        );
        return {
          ...q,
          options: q.options.filter((opt: { value?: unknown }) =>
            selectedValues.has(String(opt.value))
          ),
        };
      });
    return questionnaireMutation.mutateAsync(
      selectedQuestions as Record<string, unknown>[]
    );
  };

  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (!analyticsReady) return;
    if (!propertyId || !propertySlug) return;
    const pageUrl =
      typeof window !== "undefined" ? window.location.href : undefined;
    if (!pageUrl) return;

    const key = `${propertyId}|${pageUrl}`;
    if (lastQuestionnairePageRef.current === key) return;

    trackEvent("Page Viewed", {
      page_name: `Questionnaire`,
      page_view: pageUrl,
      property_id: propertyId,
      property_slug: propertySlug,
      property_name: propertyName,
      total_units_available: totalUnitsAvailable,
      unit_count_date: new Date().toISOString(),
    });

    lastQuestionnairePageRef.current = key;
  }, [
    analyticsReady,
    propertyId,
    propertySlug,
    propertyName,
    totalUnitsAvailable,
    trackEvent,
  ]);

  if (!filtersData) return null;

  // Questionnaire steps come from the main /filters API (includes dynamic steps when configured).
  const questionnaire: QuestionnaireItem[] =
    filtersData?.data?.questionnaire ?? [];
  const totalQuestions = questionnaire.length;

  const isWelcomeStep = stepIndex === 0;

  // Map the UI step to a QUESTION ARRAY INDEX (0..N-1) or -1 if not a question
  const questionIndex = isMobile ? stepIndex - 1 : stepIndex; // desktop shows Q1 at 0
  const isQuestionStep = questionIndex >= 0 && questionIndex < totalQuestions;

  // Complete screen position differs:
  // - Mobile screens: 0..N (welcome + N questions) then complete at N+1
  // - Desktop screens: 0..N-1 (welcome+Q1, ..., QN) then complete at N
  const isComplete = isMobile
    ? stepIndex === totalQuestions + 1
    : stepIndex === totalQuestions;

  const isWelcomeOrComplete = isWelcomeStep || isComplete;

  const activeQuestion = isQuestionStep
    ? questionnaire[questionIndex]
    : undefined;

  const progressCurrent = questionIndex;
  const progressTotal = totalQuestions;

  const currentQuestionKey = isQuestionStep
    ? questionnaire[questionIndex]?.name
    : undefined;

  // Last question?
  const isOnLastQuestion =
    isQuestionStep && questionIndex === totalQuestions - 1;

  const onViewAllUnitsClick = async () => {
    trackViewAllUnits({
      propertyId,
      propertySlug,
      propertyName,
      totalUnitsAvailable: totalUnitsAvailable,
      resultsUnitCount: totalUnitsAvailable,
      unitCountDate: new Date().toISOString(),
    });

    await Promise.allSettled([
      saveQuestionnaireData(),
      saveFiltersMutation.mutateAsync(
        store.tempFilters as Record<string, unknown>
      ),
    ]);

    store.setFiltersToDefault();
    window.location.href = `${
      window.location.origin
    }${window.location.pathname.replace(/\/$/, "")}/results${
      window.location.search
    }${window.location.hash}`;
  };

  const handleSkip = (question: QuestionnaireItem) => {
    if (!question) return;
    const profileQuestion = question?.label ?? question?.name ?? "unknown";
    const questionEnum = question?.name ?? "unknown";

    trackQuestionnaireSkip({
      propertyId,
      propertySlug,
      propertyName,
      funnelStep: stepIndex,
      profileQuestion,
      questionEnum,
    });
    // Clear temp for this question
    store.handleTempFilterChange(
      question.name as Parameters<typeof store.handleTempFilterChange>[0],
      question.component === "slider" ? null : []
    );
    if (isOnLastQuestion) {
      saveQuestionnaireData();
    }
    setStepIndex((s) => s + 1);
  };

  const handleNext = () => {
    setFiltersParams?.(store.tempFilters);

    const tempValue = currentQuestionKey
      ? (store.tempFilters as Record<string, unknown>)[currentQuestionKey]
      : undefined;
    const profileQuestion =
      activeQuestion?.label ?? activeQuestion?.name ?? "unknown";
    const questionEnum =
      currentQuestionKey ?? activeQuestion?.name ?? "unknown";

    trackQuestionnaireContinue({
      propertyId,
      propertySlug,
      propertyName,
      funnelStep: stepIndex,
      profileQuestion,
      questionEnum,
      ...buildQuestionnaireAnswerTelemetry(tempValue),
    });

    setStepIndex((s) => s + 1);
  };

  const handleBack = (question?: QuestionnaireItem) => {
    const telemetryQuestion = question ?? activeQuestion;
    const profileQuestion =
      telemetryQuestion?.label ?? telemetryQuestion?.name ?? "unknown";
    const questionEnum = telemetryQuestion?.name ?? "unknown";

    trackQuestionnaireBack({
      propertyId,
      propertySlug,
      propertyName,
      funnelStep: stepIndex,
      profileQuestion,
      questionEnum,
    });
    setStepIndex((s) => Math.max(0, s - 1));
  };

  const handleLastStepContinue = () => {
    const tempValue = currentQuestionKey
      ? (store.tempFilters as Record<string, unknown>)[currentQuestionKey]
      : undefined;
    const profileQuestion =
      activeQuestion?.label ?? activeQuestion?.name ?? "unknown";
    const questionEnum =
      currentQuestionKey ?? activeQuestion?.name ?? "unknown";

    trackQuestionnaireContinue({
      propertyId,
      propertySlug,
      propertyName,
      funnelStep: stepIndex,
      profileQuestion,
      questionEnum,
      ...buildQuestionnaireAnswerTelemetry(tempValue),
    });
    saveQuestionnaireData();
    setStepIndex(isMobile ? totalQuestions + 1 : totalQuestions);
  };

  let minHeight;

  if (isWelcomeStep && isMobile && isEmbedded) {
    minHeight = "auto";
  } else if (isWelcomeStep && isMobile && !isEmbedded) {
    minHeight = "none";
  } else {
    minHeight = "100%";
  }

  let contentPaddingTop = 12;
  if (isWelcomeStep && isMobile) contentPaddingTop = 4;
  else if (isMobile && isEmbedded) contentPaddingTop = 10;

  return (
    <Box
      css={{
        display: isMobile && !isWelcomeStep ? "block" : "flex",
        flexDirection: isMobile ? "column" : "row",

        height: isMobile
          ? "calc(100vh - var(--layout-headerHeightMobile))"
          : "calc(100vh - var(--layout-headerHeight))",

        width: "100vw",
      }}
    >
      {isMobile && !isWelcomeStep ? null : (
        <CoverMedia
          leadMedia={propertyData?.data?.lead_media}
          leadVideo={propertyData?.data?.lead_video}
        />
      )}
      <Box
        css={{
          flex: 1,
          paddingTop: contentPaddingTop,
          p: 4,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: isMobile ? "flex-start" : "center",
          minHeight: minHeight,
          height: height,
          overflowY: "auto",
          "& width": { xs: "100vw", md: "50vw" },
        }}
      >
        {!isComplete && (
          <Flex
            flexDirection="column"
            textAlign="center"
            flex={isWelcomeStep && isMobile ? "0 0 auto" : 1}
            marginY={isWelcomeStep && isMobile ? "auto" : undefined}
            marginX="auto"
            width="100%"
            gap={8}
          >
            <QuestionStepRenderer
              question={activeQuestion}
              store={
                store as Parameters<typeof QuestionStepRenderer>[0]["store"]
              }
              onNext={isOnLastQuestion ? handleLastStepContinue : handleNext}
              onBack={handleBack}
              onLastStepContinue={handleLastStepContinue}
              variant={isMobile ? "mobile" : "desktop"}
              isWelcomeStep={isWelcomeStep}
              isComplete={isComplete}
              handleSkip={handleSkip}
              currentStep={progressCurrent}
              totalSteps={progressTotal}
              propertyData={propertyData?.data}
              onViewAllUnitsClick={onViewAllUnitsClick}
              isMobile={isMobile}
              isWelcomeOrComplete={isWelcomeOrComplete}
            />
          </Flex>
        )}

        {isComplete && (
          <QuestionnaireAllSet
            filtersData={filtersData}
            setUnitsEnabled={setUnitsEnabled}
            propertyData={propertyData}
          />
        )}
      </Box>
    </Box>
  );
};
